import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

const C = {
  green: '#1B5C3A', greenDark: '#0F3D26', greenLight: '#E8F5EE',
  gold: '#F5A623', goldLight: '#FFF8E7', goldDark: '#C4831A',
  cream: '#FAF8F3', charcoal: '#1A1A1A', muted: '#6B7280',
  border: '#E5E7EB', white: '#FFFFFF', red: '#DC2626', redLt: '#FEF2F2'
};
const F = { display: "'Fraunces', Georgia, serif", body: "'Plus Jakarta Sans', sans-serif" };

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState(1); // 1: Email, 2: OTP & New Pass
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Resend OTP Timer
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.forgotPassword({ email });
      setStep(2);
      setResendTimer(60); // 60 seconds cooldown
      if (!e) setSuccess('OTP sent successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP.');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) return setError("Passwords don't match.");
    if (newPassword.length < 6) return setError("Password must be at least 6 characters.");
    
    setLoading(true);
    try {
      await authAPI.resetPassword({ email, otp, newPassword });
      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password.');
    } finally { setLoading(false); }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 12,
    border: `1.5px solid ${C.border}`,
    fontFamily: F.body,
    fontSize: 14,
    outline: 'none',
    transition: 'all 0.2s',
    boxSizing: 'border-box'
  };

  const btnStyle = {
    width: '100%',
    padding: '14px',
    borderRadius: 12,
    border: 'none',
    fontFamily: F.body,
    fontSize: 15,
    fontWeight: 700,
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8
  };

  return (
    <div style={{ minHeight: '100vh', background: C.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 440, background: C.white, padding: 40, borderRadius: 24, boxShadow: '0 20px 50px rgba(0,0,0,0.06)', position: 'relative', overflow: 'hidden' }}>
        
        {/* Subtle Decorative Circle */}
        <div style={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, borderRadius: '50%', background: C.greenLight, opacity: 0.5, zIndex: 0 }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <svg width="34" height="26" viewBox="0 0 38 30" fill="none">
                <path d="M13 2 L35 2 Q37 2 37 4 L37 26 Q37 28 35 28 L13 28 L2 15 Z" fill={C.green} />
                <circle cx="27" cy="15" r="4" stroke={C.gold} strokeWidth="2.5" fill="none" />
              </svg>
              <span style={{ fontFamily: F.display, fontSize: 26, fontWeight: 900, color: C.green, letterSpacing: -0.5 }}>Kanjusee<span style={{ color: C.gold }}>.</span></span>
            </Link>
          </div>

          <h2 style={{ fontFamily: F.display, fontSize: 32, fontWeight: 900, color: C.charcoal, textAlign: 'center', marginBottom: 12, lineHeight: 1.1 }}>
            {step === 1 ? 'Forgot Password?' : 'Reset Password'}
          </h2>
          <p style={{ fontFamily: F.body, fontSize: 14, color: C.muted, textAlign: 'center', marginBottom: 32, lineHeight: 1.6 }}>
            {step === 1 
              ? "No worries! It happens. Enter your email and we'll send you an OTP to get you back in." 
              : `Check your inbox! We've sent a 6-digit verification code to ${email}`}
          </p>

          {error && (
            <div style={{ background: C.redLt, color: C.red, padding: '12px 16px', borderRadius: 12, fontSize: 13, marginBottom: 24, fontWeight: 600, border: `1px solid ${C.red}20`, display: 'flex', gap: 8 }}>
              <span>⚠️</span> {error}
            </div>
          )}
          {success && (
            <div style={{ background: C.greenLight, color: C.green, padding: '12px 16px', borderRadius: 12, fontSize: 13, marginBottom: 24, fontWeight: 600, border: `1px solid ${C.green}20`, display: 'flex', gap: 8 }}>
              <span>✅</span> {success}
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleSendOtp}>
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.charcoal, marginBottom: 8 }}>Email Address</label>
                <input 
                  style={inputStyle} 
                  type="email" 
                  required 
                  value={email} 
                  onChange={e => setEmail(e.target.value)} 
                  placeholder="name@example.com"
                  onFocus={e => e.target.style.borderColor = C.green}
                  onBlur={e => e.target.style.borderColor = C.border}
                />
              </div>
              <button type="submit" disabled={loading} style={{ ...btnStyle, background: C.green, color: C.white }}>
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.charcoal, marginBottom: 8 }}>Verification Code</label>
                <input 
                  style={{ ...inputStyle, letterSpacing: 8, textAlign: 'center', fontSize: 20, fontWeight: 800 }} 
                  required 
                  maxLength={6}
                  value={otp} 
                  onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} 
                  placeholder="000000" 
                  onFocus={e => e.target.style.borderColor = C.green}
                  onBlur={e => e.target.style.borderColor = C.border}
                />
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  {resendTimer > 0 ? (
                    <span style={{ fontFamily: F.body, fontSize: 12, color: C.muted }}>Resend code in <strong style={{ color: C.gold }}>{resendTimer}s</strong></span>
                  ) : (
                    <button 
                      type="button" 
                      onClick={() => handleSendOtp()} 
                      disabled={loading}
                      style={{ background: 'none', border: 'none', color: C.green, fontSize: 13, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Resend Verification Code
                    </button>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.charcoal, marginBottom: 8 }}>New Password</label>
                <input 
                  style={inputStyle} 
                  type="password" 
                  required 
                  value={newPassword} 
                  onChange={e => setNewPassword(e.target.value)} 
                  placeholder="••••••••" 
                  onFocus={e => e.target.style.borderColor = C.green}
                  onBlur={e => e.target.style.borderColor = C.border}
                />
              </div>

              <div style={{ marginBottom: 28 }}>
                <label style={{ display: 'block', fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.charcoal, marginBottom: 8 }}>Confirm New Password</label>
                <input 
                  style={inputStyle} 
                  type="password" 
                  required 
                  value={confirmPassword} 
                  onChange={e => setConfirmPassword(e.target.value)} 
                  placeholder="••••••••" 
                  onFocus={e => e.target.style.borderColor = C.green}
                  onBlur={e => e.target.style.borderColor = C.border}
                />
              </div>

              <button type="submit" disabled={loading} style={{ ...btnStyle, background: C.green, color: C.white }}>
                {loading ? 'Updating...' : 'Set New Password'}
              </button>

              <button 
                type="button" 
                onClick={() => { setStep(1); setSuccess(''); setError(''); }} 
                style={{ background: 'none', border: 'none', color: C.muted, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'block', margin: '20px auto 0', transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = C.charcoal}
                onMouseLeave={e => e.target.style.color = C.muted}
              >
                ← Back to email
              </button>
            </form>
          )}

          <div style={{ textAlign: 'center', marginTop: 32, paddingTop: 24, borderTop: `1px solid ${C.grayLight}` }}>
            <p style={{ fontFamily: F.body, fontSize: 14, color: C.muted }}>
              Suddenly remembered?{' '}
              <Link to="/login" style={{ color: C.green, fontWeight: 800, textDecoration: 'none' }}>Sign In</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
